import React, { useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import { useAuth } from '../../context/AuthContext';
import { ClipboardCheck, Search, Filter, ChevronDown, CheckCircle2, Clock, XCircle, Award, Users, UserX, UserCheck } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const toTitleCase = (s) => s ? s.toLowerCase().replace(/(?:^|\s|[-/])\S/g, c => c.toUpperCase()) : '';

const formatRut = (rut) => {
  if (!rut) return '';
  const clean = rut.replace(/[.\-]/g, '');
  if (clean.length < 2) return clean;
  const body = clean.slice(0, -1);
  const dv = clean.slice(-1);
  return `${body.replace(/\B(?=(\d{3})+(?!\d))/g, '.')}-${dv.toUpperCase()}`;
};

const STATUS_CONFIG = {
  aprobado:  { label: 'Aprobado',  color: 'bg-emerald-100 text-emerald-700', icon: CheckCircle2 },
  rechazado: { label: 'Rechazado', color: 'bg-red-100 text-red-700',     icon: XCircle },
  presente:  { label: 'Presente',  color: 'bg-blue-100 text-blue-700',   icon: UserCheck },
  ausente:   { label: 'Ausente',   color: 'bg-slate-200 text-slate-600', icon: UserX },
  pendiente: { label: 'Pendiente', color: 'bg-amber-100 text-amber-700', icon: Clock },
};

const FILTER_OPTIONS = ['todos', 'aprobado', 'rechazado', 'presente', 'ausente', 'pendiente'];

export default function EseResultados() {
  const { token } = useAuth();
  const headers = { Authorization: `Bearer ${token}` };

  const [resultados, setResultados] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filtroEstado, setFiltroEstado] = useState('todos');
  const [showFilter, setShowFilter] = useState(false);

  const fetchResultados = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/ese/resultados`, { headers });
      setResultados(res.data);
    } catch { }
    setLoading(false);
  }, [token]);

  useEffect(() => { fetchResultados(); }, [fetchResultados]);

  const filtered = useMemo(() => {
    return resultados.filter(r => {
      const q = search.toLowerCase();
      const matchSearch = !q ||
        r.nombre_trabajador?.toLowerCase().includes(q) ||
        (r.rut_trabajador || '').replace(/[.\-]/g, '').includes(q.replace(/[.\-]/g, '')) ||
        r.capacitacion_nombre?.toLowerCase().includes(q);
      const matchEstado = filtroEstado === 'todos' || r.estado_resultado === filtroEstado;
      return matchSearch && matchEstado;
    });
  }, [resultados, search, filtroEstado]);

  const stats = useMemo(() => {
    const total = resultados.length;
    const aprobados = resultados.filter(r => r.estado_resultado === 'aprobado').length;
    const rechazados = resultados.filter(r => r.estado_resultado === 'rechazado').length;
    const presentes = resultados.filter(r => r.estado_resultado === 'presente').length;
    const ausentes = resultados.filter(r => r.estado_resultado === 'ausente').length;
    const pendientes = resultados.filter(r => r.estado_resultado === 'pendiente').length;
    const uniqueWorkers = new Set(resultados.map(r => r.rut_trabajador)).size;
    return { total, aprobados, rechazados, presentes, ausentes, pendientes, uniqueWorkers };
  }, [resultados]);

  if (loading) return (
    <div className="flex items-center justify-center py-20" data-testid="loading-spinner">
      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500" />
    </div>
  );

  return (
    <div className="space-y-6" data-testid="ese-resultados">
      <h1 className="text-2xl font-bold text-slate-900">Resultados de Capacitaciones</h1>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
        <div className="bg-white rounded-xl border border-slate-200 p-4" data-testid="stat-total">
          <div className="flex items-center gap-2 mb-1">
            <Users className="w-4 h-4 text-slate-400" />
            <span className="text-xs text-slate-500 font-medium">Total</span>
          </div>
          <p className="text-2xl font-bold text-slate-800">{stats.total}</p>
          <p className="text-xs text-slate-400">{stats.uniqueWorkers} trabajador(es)</p>
        </div>
        <div className="bg-white rounded-xl border border-slate-200 p-4" data-testid="stat-aprobados">
          <div className="flex items-center gap-2 mb-1">
            <CheckCircle2 className="w-4 h-4 text-emerald-500" />
            <span className="text-xs text-slate-500 font-medium">Aprobados</span>
          </div>
          <p className="text-2xl font-bold text-emerald-600">{stats.aprobados}</p>
        </div>
        <div className="bg-white rounded-xl border border-slate-200 p-4" data-testid="stat-rechazados">
          <div className="flex items-center gap-2 mb-1">
            <XCircle className="w-4 h-4 text-red-500" />
            <span className="text-xs text-slate-500 font-medium">Rechazados</span>
          </div>
          <p className="text-2xl font-bold text-red-600">{stats.rechazados}</p>
        </div>
        <div className="bg-white rounded-xl border border-slate-200 p-4" data-testid="stat-presentes">
          <div className="flex items-center gap-2 mb-1">
            <UserCheck className="w-4 h-4 text-blue-500" />
            <span className="text-xs text-slate-500 font-medium">Presentes</span>
          </div>
          <p className="text-2xl font-bold text-blue-600">{stats.presentes}</p>
        </div>
        <div className="bg-white rounded-xl border border-slate-200 p-4" data-testid="stat-ausentes">
          <div className="flex items-center gap-2 mb-1">
            <UserX className="w-4 h-4 text-slate-500" />
            <span className="text-xs text-slate-500 font-medium">Ausentes</span>
          </div>
          <p className="text-2xl font-bold text-slate-600">{stats.ausentes}</p>
        </div>
        <div className="bg-white rounded-xl border border-slate-200 p-4" data-testid="stat-pendientes">
          <div className="flex items-center gap-2 mb-1">
            <Clock className="w-4 h-4 text-amber-500" />
            <span className="text-xs text-slate-500 font-medium">Pendientes</span>
          </div>
          <p className="text-2xl font-bold text-amber-600">{stats.pendientes}</p>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-xl border border-slate-200 p-4">
        <div className="flex flex-col sm:flex-row gap-3">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-2.5 w-4 h-4 text-slate-400" />
            <input type="text" value={search} onChange={e => setSearch(e.target.value)}
              placeholder="Buscar por nombre, RUT o capacitacion..."
              className="w-full pl-9 pr-4 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-300"
              data-testid="search-resultados" />
          </div>
          <div className="relative">
            <button onClick={() => setShowFilter(!showFilter)}
              className="flex items-center gap-2 px-4 py-2 border border-slate-200 rounded-lg text-sm text-slate-600 hover:bg-slate-50 transition-colors"
              data-testid="filter-btn">
              <Filter className="w-4 h-4" />
              {filtroEstado === 'todos' ? 'Todos' : STATUS_CONFIG[filtroEstado]?.label}
              <ChevronDown className="w-3 h-3" />
            </button>
            {showFilter && (
              <div className="absolute right-0 top-full mt-1 bg-white border border-slate-200 rounded-lg shadow-lg z-20 min-w-[180px]">
                {FILTER_OPTIONS.map(opt => (
                  <button key={opt}
                    onClick={() => { setFiltroEstado(opt); setShowFilter(false); }}
                    className={`w-full text-left px-4 py-2 text-sm hover:bg-slate-50 first:rounded-t-lg last:rounded-b-lg ${filtroEstado === opt ? 'bg-blue-50 text-blue-700 font-medium' : 'text-slate-600'}`}
                    data-testid={`filter-${opt}`}>
                    {opt === 'todos' ? 'Todos los estados' : STATUS_CONFIG[opt]?.label}
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Results Table */}
      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
        {filtered.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-16 px-4">
            <Award className="w-12 h-12 text-slate-300 mb-3" />
            <p className="text-sm text-slate-500 font-medium">
              {resultados.length === 0 ? 'No hay resultados de capacitaciones aun' : 'No se encontraron resultados con los filtros aplicados'}
            </p>
            <p className="text-xs text-slate-400 mt-1">
              {resultados.length === 0 ? 'Los resultados apareceran cuando inscriba trabajadores en sesiones' : 'Intente con otros criterios de busqueda'}
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full" data-testid="resultados-table">
              <thead>
                <tr className="bg-slate-50 border-b border-slate-200">
                  <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">Trabajador</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">Capacitacion</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">Fecha Sesion</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">Lugar</th>
                  <th className="text-center px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">Asistencia</th>
                  <th className="text-center px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">Nota</th>
                  <th className="text-center px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">Resultado</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {filtered.map(r => {
                  const cfg = STATUS_CONFIG[r.estado_resultado] || STATUS_CONFIG.pendiente;
                  const IconComp = cfg.icon;
                  return (
                    <tr key={r.id} className="hover:bg-slate-50/50 transition-colors" data-testid={`resultado-row-${r.id}`}>
                      <td className="px-4 py-3">
                        <p className="text-sm font-medium text-slate-800">{toTitleCase(r.nombre_trabajador)}</p>
                        <p className="text-xs text-slate-400 font-mono">{formatRut(r.rut_trabajador)}</p>
                      </td>
                      <td className="px-4 py-3">
                        <p className="text-sm text-slate-700">{toTitleCase(r.capacitacion_nombre)}</p>
                        <p className="text-xs text-slate-400">{r.modalidad} {r.horas ? `| ${r.horas}h` : ''}</p>
                      </td>
                      <td className="px-4 py-3">
                        <p className="text-sm text-slate-700">{r.fecha_sesion.split('-').reverse().join('/')}</p>
                        <p className="text-xs text-slate-400">{r.hora_inicio} - {r.hora_fin}</p>
                      </td>
                      <td className="px-4 py-3">
                        <p className="text-sm text-slate-600">{r.lugar}</p>
                      </td>
                      <td className="px-4 py-3 text-center">
                        {r.asistencia ? (
                          <span className={`inline-flex items-center gap-1 text-xs font-medium px-2 py-0.5 rounded-full ${
                            r.asistencia === 'presente' ? 'bg-blue-100 text-blue-700' : 'bg-slate-200 text-slate-600'
                          }`}>
                            {r.asistencia === 'presente' ? <UserCheck className="w-3 h-3" /> : <UserX className="w-3 h-3" />}
                            {r.asistencia === 'presente' ? 'Presente' : 'Ausente'}
                          </span>
                        ) : (
                          <span className="text-xs text-slate-300">-</span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-center">
                        {r.nota !== null ? (
                          <span className={`text-sm font-semibold ${r.resultado === 'aprobado' ? 'text-emerald-600' : r.resultado === 'rechazado' ? 'text-red-600' : 'text-slate-700'}`}>
                            {r.nota}%
                          </span>
                        ) : (
                          <span className="text-xs text-slate-300">-</span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-center">
                        <span className={`inline-flex items-center gap-1 text-xs font-medium px-2.5 py-1 rounded-full ${cfg.color}`}>
                          <IconComp className="w-3 h-3" />
                          {cfg.label}
                        </span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
